import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { FaUser, FaPhone, FaEnvelope, FaGraduationCap, FaMapMarkerAlt, FaCommentDots } from 'react-icons/fa';
import apiService from '../services/api';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    preferred_college: '',
    course_interested: '',
    state: '',
    city: '',
    message: ''
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/[^0-9]/g, ''))) {
      newErrors.phone = 'Phone must be 10 digits';
    }
    if (!formData.preferred_college.trim()) newErrors.preferred_college = 'Preferred college is required';
    if (!formData.course_interested.trim()) newErrors.course_interested = 'Course is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill all required fields correctly');
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.submitConsultation(formData);

      if (response.data.success) {
        toast.success('Consultation request submitted successfully!');
        // Redirect to payment page
        navigate(`/payment/${response.data.consultation_id}`, {
          state: { paymentDetails: response.data.payment_details }
        });
      }
    } catch (error) {
      console.error('Error submitting consultation:', error);
      toast.error(error.response?.data?.error || 'Failed to submit consultation request');
    } finally {
      setLoading(false);
    }
  };

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan',
    'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
    'Uttarakhand', 'West Bengal'
  ];

  return (
    <>
      <Helmet>
        <title>Register for Consultation - EduConsult</title>
        <meta name="description" content="Register for personalized college admission consultation. Get expert guidance from our experienced counselors." />
      </Helmet>

      {/* Hero */}
      <section className="hero-pattern pt-32 pb-12 md:pt-40 md:pb-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              Register for <span className="gradient-text">Consultation</span>
            </h1>
            <p className="text-lg text-gray-600">
              Fill out the form below to get started with your college admission journey
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            {/* Steps Indicator */}
            <div className="bg-white rounded-xl p-6 shadow-lg mb-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold mr-3">
                    1
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Registration</div>
                    <div className="text-xs text-gray-500">Fill your details</div>
                  </div>
                </div>
                <div className="h-px flex-1 bg-gray-300 mx-4"></div>
                <div className="flex items-center opacity-50">
                  <div className="w-10 h-10 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center font-bold mr-3">
                    2
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Payment</div>
                    <div className="text-xs text-gray-500">Pay consultation fee</div>
                  </div>
                </div>
                <div className="h-px flex-1 bg-gray-300 mx-4"></div>
                <div className="flex items-center opacity-50">
                  <div className="w-10 h-10 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center font-bold mr-3">
                    3
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Consultation</div>
                    <div className="text-xs text-gray-500">Get guidance</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8 shadow-lg">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className={`w-full pl-12 input-field ${errors.name ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FaPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="10-digit mobile number"
                      className={`w-full pl-12 input-field ${errors.phone ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      className={`w-full pl-12 input-field ${errors.email ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                </div>

                {/* Preferred College */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred College <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FaGraduationCap className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="preferred_college"
                      value={formData.preferred_college}
                      onChange={handleChange}
                      placeholder="e.g., Delhi University"
                      className={`w-full pl-12 input-field ${errors.preferred_college ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {errors.preferred_college && <p className="mt-1 text-sm text-red-500">{errors.preferred_college}</p>}
                </div>

                {/* Course Interested */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Interested <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="course_interested"
                    value={formData.course_interested}
                    onChange={handleChange}
                    placeholder="e.g., B.Tech Computer Science"
                    className={`w-full input-field ${errors.course_interested ? 'border-red-500' : ''}`}
                  />
                  {errors.course_interested && <p className="mt-1 text-sm text-red-500">{errors.course_interested}</p>}
                </div>

                {/* State */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FaMapMarkerAlt className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className={`w-full pl-12 input-field ${errors.state ? 'border-red-500' : ''}`}
                    >
                      <option value="">Select State</option>
                      {indianStates.map((state) => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  {errors.state && <p className="mt-1 text-sm text-red-500">{errors.state}</p>}
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Enter your city"
                    className="w-full input-field"
                  />
                </div>

                {/* Message */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Message
                  </label>
                  <div className="relative">
                    <FaCommentDots className="absolute left-4 top-4 text-gray-400" />
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      placeholder="Tell us about your goals, preferences, or any specific requirements..."
                      className="w-full pl-12 input-field"
                    ></textarea>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Submitting...' : 'Proceed to Payment'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>

            {/* Info Box */}
            <div className="mt-8 bg-primary-50 border border-primary-200 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-2">What happens next?</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">•</span>
                  You'll be redirected to the payment page to pay a small consultation fee
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">•</span>
                  After payment verification, our expert counselor will contact you within 24 hours
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">•</span>
                  You'll receive personalized guidance for college selection and admission process
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default RegisterPage;
